/**
 * MetricDetailSheet — web parity of iOS MetricDetailSheet.
 *
 * Any dashboard number can open this: bigger presentation of the
 * value, an optional chart slot, Nova's plain-language take on
 * what the number means, and a "Go there" primary CTA.
 *
 * FIX-8 — Nova's take now comes from `nova-gateway` (the single AI
 * boundary since NOVA-ACT-1) instead of the legacy `orbit-assistant`
 * function. This was the last live web caller of orbit-assistant, and
 * it was doubly broken: the old function 500'd on a gateway-rejected
 * model string, and this component read `data.message` from a
 * response whose field is `content` — so the take never rendered
 * even when the call succeeded. The gateway streams the AI-SDK v5
 * UI-message protocol; we're a one-shot surface, so we wait for the
 * whole stream and stitch the text-delta frames together.
 *
 * Rennan's rule: no dead buttons — if it has info it opens more.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { NovaSpinner } from '@/components/nova';

export interface MetricDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: React.ComponentType<{ className?: string }>;
  eyebrow?: string;
  label: string;
  value: string | number;
  /** Short factual context passed to Nova as background. */
  context?: string;
  /** Optional chart / visualization slot. */
  chart?: ReactNode;
  cta: { label: string; href: string };
  /** Ring/tone color for icon chip. */
  accent?: string;
}

export function MetricDetailSheet({
  open,
  onOpenChange,
  icon: Icon,
  eyebrow,
  label,
  value,
  context,
  chart,
  cta,
  accent,
}: MetricDetailSheetProps) {
  const [take, setTake] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTake('');
    setError(null);
    setLoading(true);

    (async () => {
      try {
        const GATEWAY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nova-gateway`;
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        const prompt = [
          `The business is looking at the metric "${label}" which is currently ${value}.`,
          context ? `Context: ${context}` : null,
          `In one or two short Nova-voice sentences, tell them what that number means for their week and what to do next. No lists, no headers.`,
        ].filter(Boolean).join('\n');

        // UIMessage request shape — same wire format as the launcher
        // transport (src/lib/nova/context.ts) and iOS
        // NovaGatewayService.
        const resp = await fetch(GATEWAY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: [
              {
                id: crypto.randomUUID(),
                role: 'user',
                parts: [{ type: 'text', text: prompt }],
              },
            ],
            capability: 'chat',
            context: {
              surface: 'Dashboard metric detail',
              focus: `the "${label}" metric and what to do about it`,
              platform: 'web',
            },
          }),
        });
        if (!resp.ok) throw new Error(`Nova responded ${resp.status}`);
        // Collect the SSE stream and concatenate the text-delta
        // frames ({"type":"text-delta","delta":"…"} per `data:` line).
        // Unknown frame types are skipped so gateway upgrades never
        // break this surface.
        const raw = await resp.text();
        let text = '';
        for (const line of raw.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            const frame = JSON.parse(payload) as { type?: string; delta?: string };
            if (frame.type === 'text-delta' && typeof frame.delta === 'string') {
              text += frame.delta;
            }
          } catch {
            /* tolerate malformed/partial frames */
          }
        }
        if (cancelled) return;
        setTake(text.trim() || "Nothing to add — the number speaks for itself.");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Signal cut out.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, label, value, context]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          {eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {eyebrow}
            </p>
          )}
          <DialogTitle className="flex items-center gap-3">
            {Icon && (
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
                style={{
                  background: accent ? `${accent}22` : 'hsl(var(--muted))',
                  color: accent ?? 'hsl(var(--foreground))',
                }}
              >
                <Icon className="h-4 w-4" />
              </span>
            )}
            <span className="font-display text-xl font-semibold tracking-tight">
              {label}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Detail view for {label}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="rounded-2xl border border-border/60 bg-muted/30 p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Now
            </p>
            <p className="mt-1 font-display text-5xl font-semibold tabular-nums text-foreground">
              {value}
            </p>
            {chart && <div className="mt-4">{chart}</div>}
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Nova's take
            </p>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <NovaSpinner size={14} />
                Reading the tea leaves…
              </div>
            ) : error ? (
              <p className="text-sm text-muted-foreground">
                Signal's a little weak — try opening this again in a moment.
              </p>
            ) : (
              <p className="text-[15px] leading-snug text-foreground">
                {take}
              </p>
            )}
          </div>

          <Button asChild className="w-full gap-2">
            <Link to={cta.href} onClick={() => onOpenChange(false)}>
              {cta.label}
              <OrbitIcon name="arrow.right" fallback={ArrowRight} className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MetricDetailSheet;